/**
 * DashboardView — the manager's cockpit: headline numbers, active sites,
 * recent packs and annual-plan coverage at a glance.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

import { API, fetchPackForWorkspace } from "../lib/beaconUtils";
import { ScoreInfo } from "./ScoreInfo.jsx";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function DashboardView({
  appliedProfile, planId = "default", sites = [], activeSiteId, onSelectSite,
  onGenerateTopic,
  onOpenPack, onGoPlan, onGoQuick, onGoLibrary,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = { plan_id: planId };
    if (appliedProfile?.industry) {
      params.industry = appliedProfile.industry;
      params.jurisdictions = (appliedProfile.jurisdictions || []).join(",");
      if (appliedProfile.free_text_context) {
        params.free_text_context = appliedProfile.free_text_context;
      }
    }
    axios.get(`${API}/dashboard`, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [appliedProfile, planId]);

  const openPack = async (packId) => {
    try {
      onOpenPack(await fetchPackForWorkspace(packId));
    } catch (e) { alert(`Open failed: ${e.message}`); }
  };

  if (error) {
    return (
      <div className="beacon-alert" data-testid="dashboard-error">
        Dashboard failed to load: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="beacon-dashboard-loading" data-testid="dashboard-loading">
        <span className="beacon-spinner beacon-spinner-dark" />
      </div>
    );
  }

  const {
    sites: packSites, recent_packs: recentPacks, plan, totals, this_week: thisWeek,
    health,
  } = data;

  const healthTone = health
    ? (() => {
        const low = Math.min(health.avg_quality, health.avg_compliance);
        if (low >= 8) return "is-good";
        if (low >= 6) return "is-ok";
        return "is-poor";
      })()
    : "";

  return (
    <div className="beacon-dashboard" data-testid="dashboard-view">
      <div className="beacon-dashboard-head">
        <div>
          <div className="beacon-tiny-label">SITE DASHBOARD</div>
          <h2 className="beacon-dashboard-title">Where your comms program stands</h2>
        </div>
        <button type="button" className="beacon-cta beacon-cta-compact"
          data-testid="dashboard-new-pack" onClick={onGoQuick}>
          Generate a pack →
        </button>
      </div>

      {thisWeek && (
        <section
          className={`beacon-dashboard-card beacon-this-week${thisWeek.blank ? " is-blank" : ""}`}
          data-testid="dashboard-this-week">
          <div className="beacon-dashboard-card-head">
            <div className="beacon-tiny-label">
              THIS WEEK · {thisWeek.month.toUpperCase()} · WEEK {thisWeek.week}
            </div>
            <div className="beacon-this-week-head-right">
              {sites.length > 1 && (
                <select
                  className="beacon-input beacon-site-switcher-select"
                  data-testid="dashboard-site-select"
                  value={activeSiteId || ""}
                  onChange={(e) => onSelectSite(e.target.value)}
                >
                  {sites.map((s) => (
                    <option key={s.site_id} value={s.site_id}>{s.name}</option>
                  ))}
                </select>
              )}
              <div className="beacon-tiny-label beacon-this-week-theme">
                {thisWeek.current.theme_name}
              </div>
            </div>
          </div>
          <div className="beacon-this-week-row">
            <div>
              <div className="beacon-this-week-topic" data-testid="this-week-topic">
                {thisWeek.current.topic_name}
              </div>
              {thisWeek.current.topic_framing && !thisWeek.blank && (
                <div className="beacon-this-week-framing">
                  {thisWeek.current.topic_framing}
                </div>
              )}
            </div>
            <button type="button" className="beacon-cta beacon-cta-compact"
              data-testid="dashboard-generate-this-week"
              disabled={thisWeek.blank}
              title={thisWeek.blank
                ? "This week is deliberately left clear"
                : "Build the full comms pack for this week's topic"}
              onClick={() => onGenerateTopic({
                ...thisWeek.current,
                month: thisWeek.month,
                week: thisWeek.week,
              })}>
              Generate this week&rsquo;s pack →
            </button>
          </div>
        </section>
      )}

      <div className="beacon-dashboard-stats" data-testid="dashboard-stats">
        <div className="beacon-dashboard-stat">
          <span className="beacon-dashboard-stat-num" data-testid="stat-sites">
            {totals.sites}
          </span>
          <span className="beacon-dashboard-stat-label">Active sites</span>
        </div>
        <div className="beacon-dashboard-stat">
          <span className="beacon-dashboard-stat-num" data-testid="stat-packs">
            {totals.packs_live}
          </span>
          <span className="beacon-dashboard-stat-label">Packs in library</span>
        </div>
        <div className="beacon-dashboard-stat">
          <span className="beacon-dashboard-stat-num" data-testid="stat-edited-months">
            {plan.edited_months}
          </span>
          <span className="beacon-dashboard-stat-label">Months re-themed</span>
        </div>
        <div className="beacon-dashboard-stat">
          <span className="beacon-dashboard-stat-num" data-testid="stat-blank-weeks">
            {plan.blank_weeks}
          </span>
          <span className="beacon-dashboard-stat-label">Blank weeks</span>
        </div>
        <div className="beacon-dashboard-stat"
          title={health
            ? `Average Luma scores across ${health.reviewed} reviewed pack${health.reviewed === 1 ? "" : "s"}${health.unreviewed ? ` — ${health.unreviewed} not yet reviewed` : ""}`
            : "No packs reviewed yet — use ✦ Review all in the Library"}>
          <span
            className={`beacon-dashboard-stat-num beacon-health-num ${healthTone}`}
            data-testid="stat-health">
            {health
              ? <>Q{health.avg_quality} · C{health.avg_compliance}</>
              : "—"}
          </span>
          <span className="beacon-dashboard-stat-label">
            Comms health <ScoreInfo align="right" />
          </span>
        </div>
      </div>

      <div className="beacon-dashboard-grid">
        <section className="beacon-dashboard-card" data-testid="dashboard-sites">
          <div className="beacon-dashboard-card-head">
            <div className="beacon-tiny-label">ACTIVE SITES</div>
          </div>
          {packSites.length === 0 ? (
            <p className="beacon-dashboard-empty">
              No sites yet — generate your first pack and the site from your
              Context Engine profile appears here.
            </p>
          ) : (
            <ul className="beacon-dashboard-site-list">
              {packSites.map((s) => (
                <li key={s.site_id} data-testid={`dashboard-site-${s.site_id}`}>
                  <span className="beacon-dashboard-site-name">{s.site_name}</span>
                  <span className="beacon-dashboard-site-meta">
                    {s.pack_count} pack{s.pack_count === 1 ? "" : "s"} · last{" "}
                    {fmtDate(s.last_generated_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="beacon-dashboard-card" data-testid="dashboard-recent">
          <div className="beacon-dashboard-card-head">
            <div className="beacon-tiny-label">RECENT PACKS</div>
            <button type="button" className="beacon-linklike"
              data-testid="dashboard-go-library" onClick={onGoLibrary}>
              Open library →
            </button>
          </div>
          {recentPacks.length === 0 ? (
            <p className="beacon-dashboard-empty">Nothing generated yet.</p>
          ) : (
            <ul className="beacon-dashboard-pack-list">
              {recentPacks.map((p) => (
                <li key={p.pack_id}>
                  <button type="button" className="beacon-dashboard-pack"
                    data-testid={`dashboard-pack-${p.pack_id}`}
                    onClick={() => openPack(p.pack_id)}>
                    <span className="beacon-dashboard-pack-title">{p.title}</span>
                    <span className="beacon-dashboard-pack-meta">
                      {p.site_name || "No site"} · {fmtDate(p.created_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="beacon-dashboard-card" data-testid="dashboard-plan">
          <div className="beacon-dashboard-card-head">
            <div className="beacon-tiny-label">ANNUAL PLAN</div>
            <button type="button" className="beacon-linklike"
              data-testid="dashboard-go-plan" onClick={onGoPlan}>
              Open plan →
            </button>
          </div>
          <ul className="beacon-dashboard-plan-facts">
            <li>
              <strong>{plan.slots_with_overrides}</strong> of 48 weeks adjusted
              from the base plan
            </li>
            <li>
              <strong>{plan.edited_months}</strong> month theme
              {plan.edited_months === 1 ? "" : "s"} replaced
            </li>
            <li>
              <strong>{plan.blank_weeks}</strong> week
              {plan.blank_weeks === 1 ? "" : "s"} deliberately left clear
            </li>
            <li>
              <strong>{plan.total_override_writes}</strong> change
              {plan.total_override_writes === 1 ? "" : "s"} in the append-only
              history
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
